import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TreePine, Compass, PenTool, Wrench, Factory, Users, Layers, Leaf, FlagTriangleRight, Lightbulb, Award, HeartHandshake } from "lucide-react";
import SEO from "@/components/SEO";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/jlmqt0w7_WhatsApp%20Image%202026-04-24%20at%2016.24.10.jpeg";
const INTERIOR_IMG = "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/xzfs6ef1_approche-yono.jpg";

const VALEURS = [
  { icon: PenTool, title: "Qualité architecturale", text: "Une attention particulière portee au dessin, aux proportions, aux matériaux et à la qualité dès finitions." },
  { icon: Leaf, title: "Materiaux biosourcés", text: "Une conception qui privilégie le bois et les matériaux biosourcés pour proposer dès espaces plus durables, plus sains et plus cohérents." },
  { icon: Compass, title: "Sur-mesure maîtrisé", text: "Pour les projets spécifiques, une capacite a concevoir dès réponses adaptées sans perdre en lisibilité ni en qualité." },
  { icon: Layers, title: "Bois et performance", text: "Le CLT et le bois massif comme base constructive, pour associer solidité, qualité d'usage et expression architecturale." },
];

const EQUIPE = [
  { num: 1, label: "Architecture" },
  { num: 2, label: "Ingenierie bois" },
  { num: 3, label: "Construction" },
  { num: 4, label: "Accompagnement client de A à Z" },
];

const ENGAGEMENTS = [
  { num: "01", icon: TreePine, title: "Bois francais", text: "Nous privilégions le bois issu de forets francaises gérées durablement pour nos constructions CLT." },
  { num: "02", icon: FlagTriangleRight, title: "Fabrication francaise", text: "Nos modules sont concus, fabriqués et assemblés en France, avec un reseau de partenaires locaux." },
  { num: "03", icon: Leaf, title: "Durabilite", text: "Une attention portee à l'empreinte environnementale de nos modules, de la conception à la fin de vie." },
  { num: "04", icon: Lightbulb, title: "Innovation", text: "Une veille constante sur les techniques constructives, matériaux et usages pour faire évoluer notre offre." },
  { num: "05", icon: Award, title: "Qualité", text: "Un contrôle rigoureux à chaque étape de production pour garantir la qualité de chaque module YONO." },
  { num: "06", icon: HeartHandshake, title: "Accompagnement", text: "Un suivi personnalisé de nos clients tout au long du projet, de la conception à la livraison." },
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">

      {/* ═══ HERO ═══ */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img src={HERO_IMG} alt="YONO habitat bois" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/10" />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-16">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">À propos</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-3 mb-3">À propos de YONO</h1>
          <p className="font-body text-base md:text-lg text-white/70 max-w-2xl">
            Une marque d'habitats bois haut de gamme, portee par l'architecture, l'usage et la qualité du produit.
          </p>
        </div>
      </section>

      {/* ═══ NOTRE VISION ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Notre vision</span>
          </Reveal>
          <Reveal delay={80}>
            <p className="font-body text-lg md:text-xl text-soft-black/80 leading-relaxed mt-8">
              YONO est une marque partenaire d'Ideal Tiny, fabricant d'habitats bois implanté près de Nantes.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed mt-6">
              Nous développons des habitats bois innovants, conçus avec une exigence particulière de qualité, de confort et de durabilité.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed mt-6">
              Notre conviction est simple : un espace bien dessiné, bien construit et bien proportionné crée plus de valeur d'usage, plus de désirabilité et une expérience plus juste.
            </p>
          </Reveal>
          <Reveal delay={160}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed mt-6">
              Nous concevons des produits bois haut de gamme qui associent qualité architecturale, matériaux biosourcés, intelligence des plans et cohérence constructive.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed mt-6">
              Chaque habitat est pensé pour offrir un espace compact, chaleureux et parfaitement maîtrisé, où chaque choix répond à un usage réel.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed mt-6">
              Chez YONO, nous défendons une vision sobre, durable et compétitive de l'habitat bois : conjuguer esthétique, performance, confort et maîtrise économique, sans jamais renoncer à la qualité.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ NOS VALEURS ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Nos valeurs</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-4 mb-4">
              Quatre piliers qui guident notre approche du modulaire bois
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-warm-grey/30 border border-warm-grey/30 mt-12">
            {VALEURS.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-light-beige p-8 md:p-10 h-full">
                  <v.icon className="h-6 w-6 text-natural-wood mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-4">{v.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ APPROCHE / PRODUCTION ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
            <div>
              <Reveal>
                <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Approche YONO</span>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-4 mb-8">Production</h2>
              </Reveal>
              <Reveal delay={80}>
                <p className="font-body text-soft-black/70 leading-relaxed mb-6">
                  YONO s'appuie sur Ideal Tiny, partenaire industriel exclusif de la marque, pour la fabrication de ses habitats bois.
                </p>
              </Reveal>
              <Reveal delay={120}>
                <p className="font-body text-soft-black/70 leading-relaxed mb-6">
                  Implantée près de Nantes depuis 7 ans, Ideal Tiny réunit un atelier spécialisé dans la construction bois, une équipe d'une vingtaine de personnes et un bureau d'étude interne.
                </p>
              </Reveal>
              <Reveal delay={160}>
                <p className="font-body text-soft-black/70 leading-relaxed mb-6">
                  Ce partenariat associe exigence de conception, maîtrise technique et savoir-faire industriel, afin de garantir une fabrication rigoureuse et constante.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <p className="font-body text-soft-black/70 leading-relaxed">
                  Chaque habitat YONO est ainsi pensé pour être fiable, durable, bien construit et reproductible.
                </p>
              </Reveal>
            </div>
            <Reveal delay={100}>
              <div className="overflow-hidden">
                <img src={INTERIOR_IMG} alt="YONO production interieur" className="w-full h-auto object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ NOTRE EQUIPE ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Notre équipé</span>
            <p className="font-body text-lg text-soft-black/70 leading-relaxed mt-4 mb-8 max-w-2xl">
              YONO réunit dès compétences en architecture, ingénierie bois, production industrielle et gestion de projets modulaires.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-warm-grey/30 border border-warm-grey/30">
            {EQUIPE.map((e, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-light-beige p-8 md:p-10 text-center">
                  <span className="font-heading text-4xl md:text-5xl font-bold text-natural-wood/30">{e.num}</span>
                  <p className="font-heading text-sm font-medium tracking-tight text-soft-black mt-4">{e.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ NOS ENGAGEMENTS ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Nos engagements</span>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-warm-grey/30 border border-warm-grey/30 mt-12">
            {ENGAGEMENTS.map((eng, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-off-white p-8 md:p-10 h-full">
                  <div className="flex items-start gap-4 mb-5">
                    <span className="font-mono text-2xl font-light text-natural-wood/40">{eng.num}</span>
                    <eng.icon className="h-5 w-5 text-natural-wood mt-1.5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-base font-medium tracking-tight text-soft-black mb-3">{eng.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{eng.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TRAVAILLONS ENSEMBLE ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-soft-black mb-6">
              Travaillons ensemble
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="font-body text-soft-black/60 leading-relaxed mb-10 max-w-xl mx-auto">
              Vous portez un projet d'hébergement où de logement modulaire ?
              Échangeons sur vos ambitions et explorons comment YONO peut vous accompagner.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="about-contact-btn"
                className="inline-flex items-center justify-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
                Nous contacter <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/" data-testid="about-réalisations-btn"
                className="inline-flex items-center justify-center gap-2 border border-soft-black text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300">
                Voir nos réalisations
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
